// @ts-nocheck
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxStyle } from "@insite/mobius/Checkbox";
import { StyledButton } from "@insite/mobius/Clickable/Clickable";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { formSmallGridProps } from "@store/Themes/common/props/form";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

export const tableGetEllipsisCellProps = (width: number): DataTableCellProps => ({
    css: css`
        > ${StyledButton}, > ${TypographyStyle}, > ${TypographyStyle} > ${StyledButton} {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: block;
            max-width: ${pxToRem(width)};
        }

        > ${StyledButton} {
            display: inline-block;
        }
    `,
});

export const tableRemoveEllipsisCellProps = (): DataTableCellProps => ({
    css: css`
        > ${StyledButton}, > ${TypographyStyle} {
            overflow: visible;
            white-space: inherit;
            max-width: none;
        }
    `,
});

export interface TableProps {
    loadingOverlay?: LoadingOverlayProps;
    headingWrapper?: FlexWrapperPresentationProps;
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    selectionHeader?: DataTableHeaderProps;
    selectionCell?: DataTableCellProps;
    amountHeader?: DataTableHeaderProps;
    amountCell?: DataTableCellProps;
    tightHeader?: DataTableHeaderProps;
    tightCell?: DataTableCellProps;
    button?: ButtonPresentationProps;
    narrowCell?: DataTableCellProps;
}

const selectionStyles = (invert?: boolean) => css`
    text-align: center;
    line-height: 1;

    ${CheckboxStyle} {
        display: flex;

        [role="checkbox"] {
            margin: 0;
            ${invert ? `border-color: transparent !important;` : null}

            + label {
                padding: 0;
            }
        }
    }

    ${RadioStyle} {
        display: flex;

        label {
            font-size: 0;
            color: transparent;

            &::before,
            &::after {
                margin: 0;
            }
        }
    }
`;

export const tableProps: TableProps = {
    loadingOverlay: { minHeight: 150 },
    headingWrapper: {
        gap: pxToRem(16),
        verticalGap: pxToRem(8),
        extendedStyles: {
            wrapper: resultCountProps.wrapper,
            content: {
                css: css`
                    justify-content: space-between;
                `,
            },
        },
    },
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
            overflow-x: auto;
        `,
    },
    selectionHeader: {
        tight: true,
        css: selectionStyles(true),
    },
    selectionCell: {
        tight: true,
        css: selectionStyles(),
    },
    amountHeader: {
        tight: true,
        css: css`
            text-align: right;

            > ${StyledButton} {
                justify-content: flex-end;
            }
        `,
    },
    amountCell: {
        tight: true,
        css: css`
            text-align: right;
        `,
    },
    tightHeader: { tight: true },
    tightCell: {
        tight: true,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
        `,
    },
    button: {
        color: "primary",
        sizeVariant: "small",
        css: css`
            ${x => x.theme.button.primary.css}
            position: relative;
            min-width: 0;
        `,
    },
    narrowCell: {
        css: css`
            padding-top: ${pxToRem(8)};
            padding-bottom: ${pxToRem(8)};
        `,
    },
};

export interface TableFilterProps {
    wrapper?: InjectableCss;
    accordion?: AccordionPresentationProps;
    formGridContainer?: GridContainerProps;
    clearButton?: ButtonPresentationProps;
    tagWrapper?: TagWrapperProps;
}

export const tableFilterProps: TableFilterProps = {
    wrapper: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.large)}
        `,
    },
    accordion: {
        mergeCss: true,
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.base)}
        `,
    },
    formGridContainer: formSmallGridProps.gridContainer,
    clearButton: { variant: "secondary" },
};
